import React from 'react';

import { TreeViewItemModel } from './tree-view-item';

export interface TreeViewContext {
  setItems: (items: TreeViewItemModel[]) => void;
  hasChildren: (itemId: string) => boolean;
  isItemExpanded: (itemId: string) => boolean;
  expandAllItems: () => void;
  expandItem: (itemId: string) => void;
  collapseItem: (itemId: string) => void;
}

export const TreeViewContext = React.createContext<TreeViewContext>({
  setItems: (items: TreeViewItemModel[]) => {
    /* No default implementation */
  },
  hasChildren: (itemId: string) => false,
  isItemExpanded: (itemId: string) => false,
  expandAllItems: () => {
    /* No default implementation */
  },
  expandItem: (itemId: string) => {
    /* No default implementation */
  },
  collapseItem: (itemId: string) => {
    /* No default implementation */
  },
});

type OpenedItemsState = string[];
type OpenedItemsAction =
  | { type: 'EXPAND'; itemId: string }
  | { type: 'EXPAND_ALL' }
  | { type: 'COLLAPSE'; itemId: string };

type Props = {
  children: React.ReactNode;
};

const TreeViewState = (props: Props) => {
  const [items, setItems] = React.useState<TreeViewItemModel[]>([]);
  const [openedItems, dispatchOpenedItems] = React.useReducer(openedItemsReducer, []);

  function openedItemsReducer(state: OpenedItemsState, action: OpenedItemsAction) {
    switch (action.type) {
      case 'EXPAND':
        return [...state, action.itemId];

      case 'EXPAND_ALL': {
        const allParentItems = items.filter((x) => x.parent).map((x) => x.parent!.id);
        return allParentItems;
      }

      case 'COLLAPSE':
        return state.filter((x) => x !== action.itemId);
    }
  }

  const hasChildren = (itemId: string) => {
    return items.some((x) => x.parent?.id === itemId);
  };

  const isItemExpanded = (itemId: string) => {
    return openedItems.includes(itemId);
  };

  const expandAllItems = React.useCallback(() => {
    dispatchOpenedItems({ type: 'EXPAND_ALL' });
  }, []);

  const expandItem = (itemId: string) => {
    dispatchOpenedItems({ type: 'EXPAND', itemId });
  };

  const collapseItem = (itemId: string) => dispatchOpenedItems({ type: 'COLLAPSE', itemId });

  return (
    <TreeViewContext.Provider
      value={{
        setItems,
        hasChildren,
        isItemExpanded,
        expandAllItems,
        expandItem,
        collapseItem,
      }}
    >
      {props.children}
    </TreeViewContext.Provider>
  );
};

export { TreeViewState };
